import { useState } from 'react';
import { Photo, Tag } from '../../types';
import { Check, Eye, Calendar, Maximize2 } from 'lucide-react';

interface PhotoListItemProps {
  photo: Photo;
  selected?: boolean;
  showTags?: boolean;
  onClick?: () => void;
  onTagClick?: (tag: Tag) => void;
  onLightboxClick?: () => void;
  selectable?: boolean;
  showLightboxButton?: boolean;
}

export default function PhotoListItem({
  photo,
  selected = false,
  showTags = true,
  onClick,
  onTagClick,
  onLightboxClick,
  selectable = false,
  showLightboxButton = false,
}: PhotoListItemProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleTagClick = (tag: Tag, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTagClick) {
      onTagClick(tag);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div
      className={`relative group cursor-pointer transition-all duration-200 p-4 ${
        selected 
          ? 'bg-primary-50 border-l-4 border-primary-500' 
          : 'hover:bg-gray-50 border-l-4 border-transparent'
      }`}
      onClick={handleClick}
    >
      <div className="flex items-center space-x-4">
        {/* Thumbnail */}
        <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 group/thumb">
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-pulse bg-gray-200 w-full h-full"></div>
            </div>
          )}
          
          {imageError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <Eye className="h-6 w-6 text-gray-400" />
            </div>
          ) : (
            <img
              src={photo.thumbnailUrl}
              alt={photo.originalName}
              className={`w-full h-full object-cover transition-opacity duration-200 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          )}

          {/* Lightbox button */}
          {showLightboxButton && onLightboxClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onLightboxClick();
              }}
              className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 text-white flex items-center justify-center transition-all duration-200 opacity-0 group-hover/thumb:opacity-100"
              title="Agrandir l'image"
            >
              <Maximize2 className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          )}
          
          {/* Selection indicator */}
          {selectable && (
            <div
              className={`absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                selected
                  ? 'bg-primary-600 border-primary-600'
                  : 'bg-white border-gray-300 group-hover:border-primary-400'
              }`}
            >
              {selected && <Check className="h-3 w-3 sm:h-4 sm:w-4 text-white" />}
            </div>
          )}
        </div>

        {/* Photo Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-gray-900 truncate" title={photo.originalName}>
                {photo.originalName}
              </h3>
              
              <div className="mt-1 flex items-center space-x-3 text-xs text-gray-500">
                <span className="font-mono">{photo.width} × {photo.height}</span>
                <span>{formatFileSize(photo.fileSize)}</span>
                <div className="flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  <span>{formatDate(photo.uploadedAt)}</span>
                </div>
              </div>
              
              {/* Tags */}
              {showTags && (
                <div className="mt-2">
                  {photo.tags && photo.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {photo.tags.slice(0, 4).map((tag) => (
                        <button
                          key={tag.id}
                          onClick={(e) => handleTagClick(tag, e)}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 hover:bg-primary-200 transition-colors"
                          title={tag.name}
                        >
                          {tag.name}
                        </button>
                      ))}
                      {photo.tags.length > 4 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          +{photo.tags.length - 4}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 italic">Aucun tag</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}